import { useEffect, useState } from "react";
import { UserPlus, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCustomer } from "@/lib/customer";
import RemoveRecord from "@/components/RemoveRecord";
import SwitchedOffList from "@/components/SwitchedOffList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const NUMBERS = [
  ["deliveryTatDays", "Delivery TAT (days)"],
  ["completionTatDays", "Completion TAT (days)"],
  ["ldPercentPerWeek", "LD percent per week"],
  ["ldWarnDays", "LD warning days"],
  ["dispatchTriggerDays", "Dispatch trigger (working days after site ready)"],
  ["followUpIntervalDays", "Follow-up interval (days)"],
  ["kanexDaysAfterClear", "Days Kanex needs once cleared"],
];

const ROLES = ["coordinator", "manager", "admin"];

export default function SettingsPage() {
  const { user: me } = useAuth();
  const { customers, customerId } = useCustomer();
  const navigate = useNavigate();
  const isAdmin = me?.role === "admin";
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "coordinator" });
  const [busy, setBusy] = useState(false);

  const loadUsers = () => api.get("/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(errText(e)));

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch((e) => toast.error(errText(e)));
    loadUsers();
  }, [customerId]);

  const saveSettings = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", settings);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const addUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("Name, email and password are needed");
      return;
    }
    setBusy(true);
    try {
      await api.post("/users", newUser);
      toast.success("User created");
      setNewUser({ name: "", email: "", password: "", role: "coordinator" });
      await loadUsers();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const patchUser = async (id, values) => {
    try {
      await api.patch(`/users/${id}`, values);
      toast.success("User updated");
      await loadUsers();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const removeUser = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} deleted`);
      await loadUsers();
    } catch (e) {
      const message = errText(e);
      if (e?.response?.status === 409) {
        toast.error(message, {
          duration: 10000,
          action: { label: "Show their work", onClick: () => navigate(`/pos?owner=${encodeURIComponent(u.name)}`) },
        });
      } else {
        toast.error(message);
      }
    }
  };

  const switchUser = async (u, on) => {
    try {
      await api.patch(`/users/${u.id}`, { isActive: on });
      toast.success(on ? `${u.name} is back on` : `${u.name} switched off — their work stays readable`);
      await loadUsers();
    } catch (e) {
      toast.error(errText(e), { duration: 9000 });
    }
  };

  if (!settings) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">People, item types and the numbers the engine runs on.</p>
      </div>

      <div className="panel p-3 sm:p-4">
        <h2 className="text-sm font-medium">People</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                {["Name", "Email", "Role", ...(isAdmin ? ["Customers"] : []), "Active", "Reset password", "Remove"].map((h) => (
                  <th key={h} className="cell font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.filter((u) => u.isActive !== false).map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className={u.isActive === false ? "opacity-60" : ""}>
                  <td className="cell">
                    {u.name}
                    {u.isActive === false && (
                      <span
                        className="ml-2 rounded-full border border-border px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground"
                        data-testid={`user-off-marker-${u.id}`}
                      >
                        off
                      </span>
                    )}
                  </td>
                  <td className="cell font-mono text-xs">{u.email}</td>
                  <td className="cell">
                    <select
                      value={u.role}
                      onChange={(e) => patchUser(u.id, { role: e.target.value })}
                      className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                      disabled={!isAdmin}
                      data-testid={`user-role-select-${u.id}`}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </td>
                  {isAdmin && (
                    <td className="cell">
                      {u.role === "admin" ? (
                        <span className="text-xs text-muted-foreground">Every customer</span>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {customers.map((c) => {
                            const on = (u.customerIds || []).includes(c.id);
                            return (
                              <button
                                key={c.id}
                                className={`rounded-full border px-2 py-0.5 text-xs transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
                                data-testid={`user-customer-toggle-${u.id}-${c.code}`}
                                onClick={() =>
                                  patchUser(u.id, {
                                    customerIds: on
                                      ? (u.customerIds || []).filter((x) => x !== c.id)
                                      : [...(u.customerIds || []), c.id],
                                  })
                                }
                              >
                                {c.code}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  )}
                  <td className="cell">
                    <Switch
                      checked={u.isActive !== false}
                      onCheckedChange={(v) => patchUser(u.id, { isActive: v })}
                      data-testid={`user-active-toggle-${u.id}`}
                    />
                  </td>
                  <td className="cell">
                    <button
                      className="text-xs text-muted-foreground hover:underline"
                      data-testid={`user-reset-password-${u.id}`}
                      onClick={() => {
                        const pwd = window.prompt(`New password for ${u.name} (at least 6 characters)`);
                        if (pwd) patchUser(u.id, { password: pwd });
                      }}
                    >
                      Set a new password
                    </button>
                  </td>
                  <td className="cell">
                    {u.id === me?.id ? (
                      <span className="text-xs text-muted-foreground" data-testid={`user-remove-self-${u.id}`}>
                        Your own account
                      </span>
                    ) : (
                      <RemoveRecord
                        kind="person"
                        name={u.name}
                        confirmValue={u.email}
                        poLines={u.poLines || 0}
                        onDelete={() => removeUser(u)}
                        onSwitchOff={() => switchUser(u, false)}
                        testId={`user-remove-${u.id}`}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 grid gap-3 border-t border-border pt-4 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Label className="field-label">Name</Label>
            <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name-input" />
          </div>
          <div>
            <Label className="field-label">Email</Label>
            <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email-input" />
          </div>
          <div>
            <Label className="field-label">Password</Label>
            <Input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="new-user-password-input" />
          </div>
          <div>
            <Label className="field-label">Role</Label>
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              data-testid="new-user-role-select"
            >
              {ROLES.filter((r) => isAdmin || r !== "admin").map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={addUser} disabled={busy} className="w-full" data-testid="new-user-save-button">
              <UserPlus className="mr-2 h-4 w-4" />
              Add the user
            </Button>
          </div>
        </div>
      </div>

      <SwitchedOffList
        title="Switched-off people"
        records={users
          .filter((u) => u.isActive === false)
          .map((u) => ({ ...u, tag: u.email }))}
        onBringBack={(u) => switchUser(u, true)}
        testId="switched-off-people"
      />
      <div className="panel p-3 sm:p-4">
        <h2 className="text-sm font-medium">Item types</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          A type marked as needing a panel check opens the panel task when a PO is created.
        </p>
        <div className="mt-3 space-y-2">
          {settings.itemTypes.map((t, i) => (
            <div key={i} className="flex flex-wrap items-center gap-3" data-testid={`item-type-row-${i}`}>
              <Input
                value={t.name}
                onChange={(e) => {
                  const list = [...settings.itemTypes];
                  list[i] = { ...list[i], name: e.target.value };
                  set("itemTypes", list);
                }}
                className="w-40"
                data-testid={`item-type-name-input-${i}`}
              />
              <button
                className="text-xs text-muted-foreground hover:underline"
                onClick={() => set("itemTypes", settings.itemTypes.filter((_, j) => j !== i))}
                data-testid={`item-type-remove-${i}`}
              >
                Remove
              </button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => set("itemTypes", [...settings.itemTypes, { name: "" }])}
            data-testid="item-type-add-button"
          >
            Add an item type
          </Button>
        </div>
      </div>

      <div className="panel p-3 sm:p-4">
        <h2 className="text-sm font-medium">Stage wording</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          The stage name is the badge on the card. The action is the line the team reads underneath it. Change either
          and it shows everywhere at once — the stage itself is still worked out by the app, never typed.
        </p>
        <div className="mt-3 space-y-3">
          {(settings.stageText ? Object.keys(settings.stageText) : [])
            .sort((a, b) => Number(a) - Number(b))
            .map((key) => (
              <div key={key} className="grid gap-2 border-b border-border pb-3 last:border-0 sm:grid-cols-3" data-testid={`stage-text-row-${key}`}>
                <div>
                  <Label className="field-label">Stage name</Label>
                  <Input
                    value={settings.stageText[key].label || ""}
                    onChange={(e) =>
                      set("stageText", {
                        ...settings.stageText,
                        [key]: { ...settings.stageText[key], label: e.target.value },
                      })
                    }
                    data-testid={`stage-label-input-${key}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="field-label">Next action</Label>
                  <Textarea
                    rows={2}
                    value={settings.stageText[key].action || ""}
                    onChange={(e) =>
                      set("stageText", {
                        ...settings.stageText,
                        [key]: { ...settings.stageText[key], action: e.target.value },
                      })
                    }
                    data-testid={`stage-action-input-${key}`}
                  />
                </div>
              </div>
            ))}
        </div>
        <Button onClick={saveSettings} disabled={busy} className="mt-3" data-testid="stage-text-save-button">
          <Save className="mr-2 h-4 w-4" />
          Save the wording
        </Button>
      </div>

      <div className="panel p-3 sm:p-4">
        <h2 className="text-sm font-medium">Numbers</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {NUMBERS.map(([key, label]) => (
            <div key={key}>
              <Label className="field-label">{label}</Label>
              <Input
                type="number"
                value={settings[key] ?? ""}
                onChange={(e) => set(key, e.target.value === "" ? "" : Number(e.target.value))}
                className="font-mono"
                data-testid={`settings-${key}-input`}
              />
            </div>
          ))}
          <div>
            <Label className="field-label">Escalation days (comma separated)</Label>
            <Input
              value={(settings.escalationDays || []).join(", ")}
              onChange={(e) =>
                set(
                  "escalationDays",
                  e.target.value.split(",").map((v) => Number(v.trim())).filter((v) => !isNaN(v))
                )
              }
              className="font-mono"
              data-testid="settings-escalationDays-input"
            />
          </div>
        </div>
        <Button onClick={saveSettings} disabled={busy} className="mt-4" data-testid="settings-save-button">
          <Save className="mr-2 h-4 w-4" />
          Save settings
        </Button>
      </div>
    </div>
  );
}
